import React, { useRef, useState, useEffect, useCallback } from 'react';

// Capture interval: 150 ms ≈ 6–7 fps — fast enough for safety monitoring,
// slow enough to give the server time to run inference.
const CAPTURE_INTERVAL_MS = 150;

export default function WebcamViewer({ fps, sendMessage, onActiveChange }) {
  const videoRef    = useRef(null);
  const captureRef  = useRef(null);   // offscreen canvas for JPEG encoding
  const intervalRef = useRef(null);
  const streamRef   = useRef(null);
  const [active, setActive]   = useState(false);
  const [error,  setError]    = useState(null);

  const setActiveState = useCallback((val) => {
    setActive(val);
    onActiveChange?.(val);
  }, [onActiveChange]);

  const startCamera = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'environment' },
        audio: false,
      });
      streamRef.current = stream;

      const video = videoRef.current;

      // Set handler BEFORE srcObject — live streams fire loadedmetadata immediately
      // on srcObject assignment, so registering after would miss the event.
      await new Promise(resolve => {
        video.onloadedmetadata = () => {
          const c = document.createElement('canvas');
          c.width  = video.videoWidth;
          c.height = video.videoHeight;
          captureRef.current = c;
          resolve();
        };
        video.srcObject = stream;
      });

      // Non-blocking: hidden video may briefly reject play() in Chrome; ignore it.
      video.play().catch(() => {});

      intervalRef.current = setInterval(() => {
        const video  = videoRef.current;
        const canvas = captureRef.current;
        if (!video || !canvas || video.readyState < 2) return;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
        sendMessage({ type: 'camera_frame', data: dataUrl.split(',')[1] });
      }, CAPTURE_INTERVAL_MS);

      setActiveState(true);
    } catch (err) {
      const isPermission = err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError';
      const isSecure     = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
      setError(
        isPermission
          ? 'Camera permission denied. Please allow camera access in your browser.'
          : !isSecure
          ? 'Camera requires a secure connection (HTTPS). ' +
            'In Chrome go to chrome://flags/#unsafely-treat-insecure-origin-as-secure, ' +
            `add "${window.location.origin}" and relaunch.`
          : `Camera error: ${err.message}`
      );
    }
  }, [sendMessage, setActiveState]);

  const stopCamera = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    captureRef.current = null;
    sendMessage({ type: 'webcam_stop' });
    setActiveState(false);
  }, [sendMessage, setActiveState]);

  // Clean up on unmount
  useEffect(() => () => {
    clearInterval(intervalRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
  }, []);

  return (
    <div className="flex flex-col gap-3">
      {/* Hidden video element — only used to grab frames */}
      <video ref={videoRef} className="hidden" playsInline muted />

      <button onClick={active ? stopCamera : startCamera}
        className={`w-full py-2 px-3 text-white text-sm font-medium rounded-lg
                    transition-colors flex items-center justify-center gap-2
                    ${active ? 'bg-red-600 hover:bg-red-700' : 'bg-green-700 hover:bg-green-600'}`}>
        {active ? '⏹ Stop Camera' : '📹 Start Camera'}
      </button>

      {active && (
        <div className="bg-surface rounded-lg px-3 py-2 text-xs flex justify-between items-center">
          <span className="text-green-400 flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            Live — browser camera
          </span>
          <span className="text-slate-400 font-mono">{fps} FPS</span>
        </div>
      )}

      {error && (
        <div className="text-xs text-red-300 bg-red-900/30 border border-red-700/50 rounded-lg p-2 leading-relaxed">
          {error}
        </div>
      )}

      {!active && !error && (
        <p className="text-xs text-slate-500 leading-relaxed">
          Captures from your device's camera. Frames are sent to the server for real-time inference.
        </p>
      )}
    </div>
  );
}
